import { useMemo } from 'react';
import { getStudentData } from '../assets/data';
import { getLessonsFromLocalStorage } from '../utils/lessonsService';

// Extracted from StudentProfile.js (Phase 3 of the platform-health plan:
// getting the file under ~600 lines needs pulling logic into hooks, not
// just JSX into components). Purely a derived computation — no state, no
// side effects besides its own (pre-existing, left as-is) debug logging —
// so this was the lowest-risk of the extractions: lessons/money this
// week/month + planned income, for the profile banner's stat cards.
export function useStudentStats({ studentId, statsUpdateTrigger, allStudentScheduleItems }) {
  return useMemo(() => {
    if (!studentId || studentId === 'new') {
      return {
        lessonsThisWeek: 0,
        lessonsThisMonth: 0,
        moneyThisMonth: 0,
        plannedIncome: 0
      };
    }

    try {
      const currentStudentData = getStudentData()[studentId];

      if (!currentStudentData) {
        return {
          lessonsThisWeek: 0,
          lessonsThisMonth: 0,
          moneyThisMonth: 0,
          plannedIncome: 0
        };
      }

      // Функция для парсинга цены (как в Home)
      const parsePrice = (priceValue) => {
        // Если это уже число, возвращаем его
        if (typeof priceValue === 'number') {
          return priceValue;
        }
        // Если это строка, парсим её
        if (typeof priceValue === 'string') {
          if (!priceValue) return 0;
          const match = priceValue.match(/\d+(\.\d+)?/);
          return match ? parseFloat(match[0]) : 0;
        }
        // Если есть priceAmount, используем его
        if (currentStudentData.priceAmount) {
          const amount = parseFloat(currentStudentData.priceAmount);
          return isNaN(amount) ? 0 : amount;
        }
        return 0;
      };

      const pricePerLesson = parsePrice(currentStudentData.price || currentStudentData.priceAmount);

      // Объединяем данные из расписания (все уроки) и localStorage
      const savedLessons = getLessonsFromLocalStorage();
      const studentIdForLessons = currentStudentData?.studentId || currentStudentData?.id || studentId;

      console.log('💰 calculateStudentStats DEBUG:', {
        studentId,
        studentIdForLessons,
        pricePerLesson,
        priceRaw: currentStudentData.price,
        priceCurrency: currentStudentData.priceCurrency,
        priceAmount: currentStudentData.priceAmount,
        allStudentScheduleItemsCount: allStudentScheduleItems.length,
        allStudentScheduleItems: allStudentScheduleItems,
        savedLessonsCount: savedLessons.length,
        currentStudentDataKeys: Object.keys(currentStudentData)
      });

      // Используем allStudentScheduleItems (все уроки из расписания) + localStorage как fallback
      const allLessons = [
        ...allStudentScheduleItems, // Все уроки из расписания Supabase (включая прошедшие)
        ...savedLessons.filter(lesson =>
          !allStudentScheduleItems.some(scheduleLesson => scheduleLesson.id === lesson.id) &&
          (String(lesson.studentId) === String(studentId) || String(lesson.studentId) === String(studentIdForLessons))
        )
      ];

      console.log('💰 allLessons after merge:', {
        totalCount: allLessons.length,
        lessons: allLessons.map(l => ({
          id: l.id,
          date: l.date,
          status: l.status,
          studentId: l.studentId,
          type: l.type
        }))
      });

      // Уроки на этой неделе (как в Home)
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1); // Monday
      startOfWeek.setHours(0, 0, 0, 0);
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
      endOfWeek.setHours(23, 59, 59, 999);

      const lessonsThisWeek = allLessons.reduce((count, lesson) => {
        if (!lesson.date || lesson.type === 'event') return count;
        const lessonStudentId = String(lesson.studentId || '').trim();
        if (lessonStudentId !== String(studentId) && lessonStudentId !== String(studentIdForLessons)) return count;

        // Парсим дату правильно (поддерживаем разные форматы)
        let lessonDate;
        if (typeof lesson.date === 'string') {
          if (lesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            lessonDate = new Date(lesson.date + 'T00:00:00');
          } else if (lesson.date.includes('T')) {
            lessonDate = new Date(lesson.date);
          } else {
            lessonDate = new Date(lesson.date);
          }
        } else if (lesson.date instanceof Date) {
          lessonDate = lesson.date;
        } else {
          return count;
        }

        if (isNaN(lessonDate.getTime())) return count;

        if (lessonDate >= startOfWeek && lessonDate <= endOfWeek) {
          // Calculate lesson duration in hours - используем timeFrom/timeTo если from/to нет
          const fromTime = lesson.from || lesson.timeFrom || '0:0';
          const toTime = lesson.to || lesson.timeTo || '1:0';
          const startTime = fromTime.split(':');
          const endTime = toTime.split(':');
          const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
          const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
          const durationMinutes = endMinutes - startMinutes;
          const durationHours = durationMinutes / 60;
          return count + Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        }
        return count;
      }, 0);

      // Уроки в этом месяце (как в Home)
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      const lessonsThisMonth = allLessons.reduce((count, lesson) => {
        if (!lesson.date || lesson.type === 'event') return count;
        const lessonStudentId = String(lesson.studentId || '').trim();
        if (lessonStudentId !== String(studentId) && lessonStudentId !== String(studentIdForLessons)) return count;

        // Парсим дату правильно (поддерживаем разные форматы)
        let lessonDate;
        if (typeof lesson.date === 'string') {
          if (lesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            lessonDate = new Date(lesson.date + 'T00:00:00');
          } else if (lesson.date.includes('T')) {
            lessonDate = new Date(lesson.date);
          } else {
            lessonDate = new Date(lesson.date);
          }
        } else if (lesson.date instanceof Date) {
          lessonDate = lesson.date;
        } else {
          return count;
        }

        if (isNaN(lessonDate.getTime())) return count;

        if (lessonDate.getMonth() === currentMonth && lessonDate.getFullYear() === currentYear) {
          // Calculate lesson duration in hours - используем timeFrom/timeTo если from/to нет
          const fromTime = lesson.from || lesson.timeFrom || '0:0';
          const toTime = lesson.to || lesson.timeTo || '1:0';
          const startTime = fromTime.split(':');
          const endTime = toTime.split(':');
          const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
          const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
          const durationMinutes = endMinutes - startMinutes;
          const durationHours = durationMinutes / 60;
          return count + Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour
        }
        return count;
      }, 0);

      // Реальный доход за месяц (как в Home)
      const moneyThisMonth = allLessons.reduce((sum, lesson) => {
        if (!lesson.date || lesson.type === 'event' || lesson.status !== 'done') {
          if (lesson.date && lesson.type !== 'event') {
            console.log('💰 moneyThisMonth skip:', {
              reason: lesson.status !== 'done' ? 'status not done' : 'no date or event',
              status: lesson.status,
              date: lesson.date,
              type: lesson.type
            });
          }
          return sum;
        }
        const lessonStudentId = String(lesson.studentId || '').trim();
        if (lessonStudentId !== String(studentId) && lessonStudentId !== String(studentIdForLessons)) {
          console.log('💰 moneyThisMonth skip - wrong student:', {
            lessonStudentId,
            studentId,
            studentIdForLessons
          });
          return sum;
        }

        // Парсим дату правильно (поддерживаем разные форматы)
        let lessonDate;
        if (typeof lesson.date === 'string') {
          if (lesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // YYYY-MM-DD format
            lessonDate = new Date(lesson.date + 'T00:00:00');
          } else if (lesson.date.includes('T')) {
            // ISO format with time
            lessonDate = new Date(lesson.date);
          } else {
            // Try to parse as-is (может быть toDateString() формат)
            lessonDate = new Date(lesson.date);
          }
        } else if (lesson.date instanceof Date) {
          lessonDate = lesson.date;
        } else {
          return sum;
        }

        if (isNaN(lessonDate.getTime())) {
          console.log('💰 moneyThisMonth skip - invalid date:', lesson.date);
          return sum;
        }

        if (lessonDate.getMonth() !== currentMonth || lessonDate.getFullYear() !== currentYear) {
          console.log('💰 moneyThisMonth skip - wrong month:', {
            lessonMonth: lessonDate.getMonth(),
            currentMonth,
            lessonYear: lessonDate.getFullYear(),
            currentYear,
            lessonDate: lessonDate.toISOString()
          });
          return sum;
        }

        // Calculate lesson duration in hours - используем timeFrom/timeTo если from/to нет
        const fromTime = lesson.from || lesson.timeFrom || '0:0';
        const toTime = lesson.to || lesson.timeTo || '1:0';
        const startTime = fromTime.split(':');
        const endTime = toTime.split(':');
        const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
        const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        const lessonCount = Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour

        const lessonValue = pricePerLesson * lessonCount;
        console.log('💰 moneyThisMonth adding:', {
          lessonId: lesson.id,
          date: lesson.date,
          durationHours,
          lessonCount,
          pricePerLesson,
          lessonValue,
          total: sum + lessonValue
        });

        return sum + lessonValue;
      }, 0);

      // Планируемый доход за месяц (как в Home)
      const plannedIncome = allLessons.reduce((sum, lesson) => {
        if (!lesson.date || lesson.type === 'event') {
          if (lesson.date) {
            console.log('💰 plannedIncome skip:', {
              reason: 'no date or event',
              date: lesson.date,
              type: lesson.type
            });
          }
          return sum;
        }
        const lessonStudentId = String(lesson.studentId || '').trim();
        if (lessonStudentId !== String(studentId) && lessonStudentId !== String(studentIdForLessons)) {
          console.log('💰 plannedIncome skip - wrong student:', {
            lessonStudentId,
            studentId,
            studentIdForLessons
          });
          return sum;
        }

        // Парсим дату правильно (поддерживаем разные форматы)
        let lessonDate;
        if (typeof lesson.date === 'string') {
          if (lesson.date.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // YYYY-MM-DD format
            lessonDate = new Date(lesson.date + 'T00:00:00');
          } else if (lesson.date.includes('T')) {
            // ISO format with time
            lessonDate = new Date(lesson.date);
          } else {
            // Try to parse as-is (может быть toDateString() формат)
            lessonDate = new Date(lesson.date);
          }
        } else if (lesson.date instanceof Date) {
          lessonDate = lesson.date;
        } else {
          return sum;
        }

        if (isNaN(lessonDate.getTime())) {
          console.log('💰 plannedIncome skip - invalid date:', lesson.date);
          return sum;
        }

        if (lessonDate.getMonth() !== currentMonth || lessonDate.getFullYear() !== currentYear) {
          console.log('💰 plannedIncome skip - wrong month:', {
            lessonMonth: lessonDate.getMonth(),
            currentMonth,
            lessonYear: lessonDate.getFullYear(),
            currentYear,
            lessonDate: lessonDate.toISOString()
          });
          return sum;
        }

        // Calculate lesson duration in hours - используем timeFrom/timeTo если from/to нет
        const fromTime = lesson.from || lesson.timeFrom || '0:0';
        const toTime = lesson.to || lesson.timeTo || '1:0';
        const startTime = fromTime.split(':');
        const endTime = toTime.split(':');
        const startMinutes = parseInt(startTime[0]) * 60 + parseInt(startTime[1]);
        const endMinutes = parseInt(endTime[0]) * 60 + parseInt(endTime[1]);
        const durationMinutes = endMinutes - startMinutes;
        const durationHours = durationMinutes / 60;
        const lessonCount = Math.max(1, Math.round(durationHours)); // At least 1 lesson, round to nearest hour

        const lessonValue = pricePerLesson * lessonCount;
        console.log('💰 plannedIncome adding:', {
          lessonId: lesson.id,
          date: lesson.date,
          status: lesson.status,
          durationHours,
          lessonCount,
          pricePerLesson,
          lessonValue,
          total: sum + lessonValue
        });

        return sum + lessonValue;
      }, 0);

      console.log('💰 Final stats:', {
        moneyThisMonth,
        plannedIncome,
        lessonsThisMonth
      });

      return {
        lessonsThisWeek,
        lessonsThisMonth,
        moneyThisMonth: Math.round(moneyThisMonth),
        plannedIncome: Math.round(plannedIncome)
      };
    } catch (error) {
      console.error('Error calculating student stats:', error);
      return {
        lessonsThisWeek: 0,
        lessonsThisMonth: 0,
        moneyThisMonth: 0,
        plannedIncome: 0
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, statsUpdateTrigger, allStudentScheduleItems]);
}
